import { select } from "d3";

import {
  AppletElement,
  EDGE_STROKE,
  EdgeEndpoints,
  GroupSelection,
  NODE_STROKE,
  Point,
  SvgSelection,
  renderApplet,
  trimmedEdgeEndpoints,
} from "./common";

type DoubleStarNodeKind = "center" | "leaf";
type DoubleStarNode = Readonly<{
  id: string;
  label?: string;
  x: number;
  y: number;
  kind: DoubleStarNodeKind;
}>;

type DoubleStarEdge = Readonly<{
  source: string;
  target: string;
}>;

const DOUBLE_STAR_WIDTH = 620;
const DOUBLE_STAR_HEIGHT = 230;
const CENTER_RADIUS = 18;
const LEAF_RADIUS = 8;
const CENTER_FILL = "#8fbc8f";
const LEAF_FILL = "#c9ddc5";
const LABEL_FILL = "#263726";
const ANNOTATION_FILL = "#666";

const DOUBLE_STAR_NODES: readonly DoubleStarNode[] = [
  { id: "x", label: "x", x: 246, y: 112, kind: "center" },
  { id: "y", label: "y", x: 374, y: 112, kind: "center" },
  { id: "l1", x: 92, y: 54, kind: "leaf" },
  { id: "l2", x: 78, y: 112, kind: "leaf" },
  { id: "l3", x: 92, y: 170, kind: "leaf" },
  { id: "l4", x: 180, y: 42, kind: "leaf" },
  { id: "r1", x: 528, y: 70, kind: "leaf" },
  { id: "r2", x: 542, y: 112, kind: "leaf" },
  { id: "r3", x: 528, y: 154, kind: "leaf" },
];

const DOUBLE_STAR_EDGES: readonly DoubleStarEdge[] = [
  { source: "x", target: "y" },
  { source: "x", target: "l1" },
  { source: "x", target: "l2" },
  { source: "x", target: "l3" },
  { source: "x", target: "l4" },
  { source: "y", target: "r1" },
  { source: "y", target: "r2" },
  { source: "y", target: "r3" },
];

function nodeLookup(): ReadonlyMap<string, DoubleStarNode> {
  return new Map(DOUBLE_STAR_NODES.map((node: DoubleStarNode): [string, DoubleStarNode] => [node.id, node]));
}

function nodeRadius(node: DoubleStarNode): number {
  return node.kind === "center" ? CENTER_RADIUS : LEAF_RADIUS;
}

function nodeFill(node: DoubleStarNode): string {
  return node.kind === "center" ? CENTER_FILL : LEAF_FILL;
}

function edgeEndpoints(edge: DoubleStarEdge, nodes: ReadonlyMap<string, DoubleStarNode>): EdgeEndpoints {
  const source = nodes.get(edge.source);
  const target = nodes.get(edge.target);
  if (source === undefined || target === undefined) {
    return { x1: 0, y1: 0, x2: 0, y2: 0 };
  }

  return trimmedEdgeEndpoints(source, target, nodeRadius(source), nodeRadius(target));
}

function appendSvg(element: AppletElement): SvgSelection {
  return select<AppletElement, unknown>(element)
    .append<SVGSVGElement>("svg")
    .attr("viewBox", `0 0 ${DOUBLE_STAR_WIDTH} ${DOUBLE_STAR_HEIGHT}`)
    .attr("width", DOUBLE_STAR_WIDTH)
    .attr("height", DOUBLE_STAR_HEIGHT)
    .attr("role", "img")
    .attr("aria-label", "A double star D four comma three with two adjacent center vertices and seven leaves.");
}

function renderEdges(graph: GroupSelection): void {
  const nodes = nodeLookup();

  graph.append<SVGGElement>("g")
    .attr("class", "double-star-example-edges")
    .selectAll<SVGLineElement, DoubleStarEdge>("line")
    .data(DOUBLE_STAR_EDGES)
    .join("line")
    .attr("x1", (edge: DoubleStarEdge): number => edgeEndpoints(edge, nodes).x1)
    .attr("y1", (edge: DoubleStarEdge): number => edgeEndpoints(edge, nodes).y1)
    .attr("x2", (edge: DoubleStarEdge): number => edgeEndpoints(edge, nodes).x2)
    .attr("y2", (edge: DoubleStarEdge): number => edgeEndpoints(edge, nodes).y2)
    .attr("stroke", EDGE_STROKE)
    .attr("stroke-width", 1.3)
    .attr("stroke-linecap", "round");
}

function renderNodes(graph: GroupSelection): void {
  const nodeGroups = graph.append<SVGGElement>("g")
    .attr("class", "double-star-example-nodes")
    .selectAll<SVGGElement, DoubleStarNode>("g")
    .data(DOUBLE_STAR_NODES)
    .join("g")
    .attr("transform", (node: DoubleStarNode): string => `translate(${node.x},${node.y})`);

  nodeGroups.append<SVGCircleElement>("circle")
    .attr("r", (node: DoubleStarNode): number => nodeRadius(node))
    .attr("fill", (node: DoubleStarNode): string => nodeFill(node))
    .attr("stroke", NODE_STROKE)
    .attr("stroke-width", (node: DoubleStarNode): number => (node.kind === "center" ? 1.8 : 1.2))
    .attr("data-node-kind", (node: DoubleStarNode): DoubleStarNodeKind => node.kind);

  nodeGroups.filter((node: DoubleStarNode): boolean => node.label !== undefined)
    .append<SVGTextElement>("text")
    .attr("text-anchor", "middle")
    .attr("dy", "0.33em")
    .attr("font-family", "Times, serif")
    .attr("font-size", 17)
    .attr("fill", LABEL_FILL)
    .text((node: DoubleStarNode): string => node.label ?? "");
}

function renderAnnotation(graph: GroupSelection, text: string, point: Point): void {
  graph.append<SVGTextElement>("text")
    .attr("x", point.x)
    .attr("y", point.y)
    .attr("text-anchor", "middle")
    .attr("font-family", "Avenir Next, Helvetica, Arial, sans-serif")
    .attr("font-size", 13)
    .attr("fill", ANNOTATION_FILL)
    .text(text);
}

function renderDoubleStarExample(element: AppletElement): void {
  element.dataset.rendered = "true";
  element.replaceChildren();

  const graph = appendSvg(element)
    .append<SVGGElement>("g")
    .attr("class", "double-star-example-graph");

  renderEdges(graph);
  renderNodes(graph);
  renderAnnotation(graph, "a = 4 leaves", { x: 142, y: 210 });
  renderAnnotation(graph, "b = 3 leaves", { x: 490, y: 210 });
}

renderApplet(".double-star-example-applet", renderDoubleStarExample);
